import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, DollarSign, Clock, User, Upload, FileText } from 'lucide-react';
import {
  formatCurrency,
  formatTimeDisplay,
  uploadAccept,
  VENDOR_DOCUMENT_FILE_TYPES,
} from '@peles/shared';
import {
  useVendorEvents,
  useRefreshMyPortalTasks,
  useVendorEventExpenses,
  useVendorEventTimeline,
  useVendorProfile,
  useUpdateVendorProfile,
  useUploadVendorDocument,
} from '../hooks/usePortal';
import { StatusPill, SearchableListSelect } from '../components/common';
import { AcceptedFileTypesNote } from '../components/common/AcceptedFileTypesNote';
import { extractApiErrorMessage } from '../utils/apiError';
import { PortalConversations } from '../components/portal/PortalConversations';
import { VendorFilesPanel } from '../components/portal/VendorFilesPanel';
import { VendorFloorPlansPanel } from '../components/portal/VendorFloorPlansPanel';
import { VendorItemsPanel } from '../components/portal/VendorItemsPanel';
import { VendorBookingCard } from '../components/portal/VendorBookingCard';
import { ClientTaskList } from '../components/portal/ClientTaskList';
import { MyTasksCard } from '../components/portal/MyTasksCard';
import type { VendorEvent, VendorProfile } from '../api/portal';
import { PhoneInput } from '../components/common/PhoneInput';
import { PhoneLink } from '../components/common/PhoneLink';

/** What the office shared of an event, in a line: "Expenses · Files · Conversations". */
export function sharedSummary(event: VendorEvent): string {
  const parts = [
    event.access.expenses && 'Expenses',
    event.access.items && 'Items',
    event.access.timeline && 'Timeline',
    event.access.folders.length > 0 && 'Files',
    event.access.floorPlans.length > 0 &&
      (event.access.floorPlans.length === 1 ? 'Floor plan' : 'Floor plans'),
    event.canMessage && 'Conversations',
  ].filter((part): part is string => !!part);
  return parts.join(' · ');
}

function VendorEventCard({
  event,
  onSelect,
  isSelected,
}: {
  event: VendorEvent;
  onSelect: (event: VendorEvent) => void;
  isSelected: boolean;
}) {
  const dateStr = new Date(event.eventDate).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      onClick={() => onSelect(event)}
      data-testid={`vendor-event-card-${event.eventId}`}
      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <h3 className="font-semibold text-lg">{event.eventName}</h3>
      <div className="mt-2 space-y-1 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span>{dateStr}</span>
        </div>
        {event.eventTypeName && (
          <div>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {event.eventTypeName}
            </span>
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {event.categories.map((cat) => (
            <span key={cat} className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              {cat}
            </span>
          ))}
        </div>
        {event.access.expenses && (
          <div className="flex items-center gap-4 mt-2">
            <span className="text-xs">
              Quoted: <strong>{formatCurrency(event.totalQuoted)}</strong>
            </span>
            <span className="text-xs">
              Paid: <strong className="text-green-600">{formatCurrency(event.totalPaid)}</strong>
            </span>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">{sharedSummary(event)}</p>
      </div>
    </button>
  );
}

function VendorExpensesPanel({ eventId }: { eventId: string }) {
  const { data: expenses, isLoading } = useVendorEventExpenses(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading expenses...</p>;
  if (!expenses || expenses.length === 0)
    return <p className="text-sm text-gray-500">No expense items for this event.</p>;

  return (
    <div data-testid="vendor-expenses">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Your Expenses
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Price</th>
              <th className="text-right p-2">Paid</th>
              <th className="text-right p-2">Balance</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((item) => (
              <tr key={item.id} className="border-t" data-testid={`expense-row-${item.id}`}>
                <td className="p-2">{item.itemName || '-'}</td>
                <td className="p-2">{item.vendorCategoryName || '-'}</td>
                <td className="p-2 text-right">${item.price.toLocaleString()}</td>
                <td className="p-2 text-right text-green-600">
                  ${item.totalPaid.toLocaleString()}
                </td>
                <td className="p-2 text-right text-red-600">${item.balance.toLocaleString()}</td>
                <td className="p-2">
                  {item.statusName && (
                    <StatusPill label={item.statusName} color={item.statusColor} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorTimelinePanel({ eventId }: { eventId: string }) {
  const { data: entries, isLoading } = useVendorEventTimeline(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading timeline...</p>;
  if (!entries || entries.length === 0)
    return <p className="text-sm text-gray-500">No timeline entries assigned to you.</p>;

  return (
    <div data-testid="vendor-timeline">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <Clock size={18} />
        Your Timeline
      </h3>
      <div className="space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="p-3 bg-gray-50 rounded-lg"
            data-testid={`timeline-entry-${entry.id}`}
          >
            <div className="flex items-center gap-2">
              {entry.time && (
                <span className="font-mono text-sm font-bold">{formatTimeDisplay(entry.time)}</span>
              )}
              <span className="font-medium">{entry.title}</span>
            </div>
            {entry.sectionName && <p className="text-xs text-gray-500 mt-1">{entry.sectionName}</p>}
            {entry.location && (
              <p className="text-xs text-gray-600 mt-1">Location: {entry.location}</p>
            )}
            {entry.vendorNote && (
              <p className="text-xs text-blue-600 mt-1">Note: {entry.vendorNote}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const DOCUMENT_TYPES = [
  { value: 'quote', label: 'Quote' },
  { value: 'invoice', label: 'Invoice' },
] as const;

function VendorDocumentUploadPanel({ eventId }: { eventId: string }) {
  const uploadMutation = useUploadVendorDocument();
  const { data: expenses = [] } = useVendorEventExpenses(eventId);
  const [docType, setDocType] = useState<'quote' | 'invoice'>('quote');
  const [file, setFile] = useState<File | null>(null);
  const [chosenExpenseId, setChosenExpenseId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [inputKey, setInputKey] = useState(0);

  // One expense needs no choosing; with several, the vendor says which.
  const expenseItemId = chosenExpenseId ?? (expenses.length === 1 ? expenses[0]!.id : null);

  function handleUpload() {
    if (!file || !expenseItemId) return;
    setMessage(null);
    uploadMutation.mutate(
      { eventId, file, documentType: docType, expenseItemId },
      {
        onSuccess: () => {
          setMessage({ tone: 'ok', text: `${file.name} was sent to the office.` });
          setFile(null);
          setInputKey((k) => k + 1);
        },
        onError: (err) => {
          setMessage({
            tone: 'error',
            text: extractApiErrorMessage(err, 'The document could not be uploaded.'),
          });
        },
      },
    );
  }

  if (expenses.length === 0) return null;

  return (
    <div data-testid="vendor-documents">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <FileText size={18} />
        Upload Documents
      </h3>
      <div className="space-y-3">
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Document Type</span>
          <SearchableListSelect
            items={[...DOCUMENT_TYPES]}
            value={docType}
            onChange={(v) => setDocType(v as 'quote' | 'invoice')}
            getValue={(t) => t.value}
            getLabel={(t) => t.label}
            ariaLabel="Document type"
          />
        </div>
        {expenses.length > 1 && (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">For</span>
            <SearchableListSelect
              items={expenses}
              value={expenseItemId}
              onChange={(v) => setChosenExpenseId(v as string)}
              getValue={(e) => e.id}
              getLabel={(e) => e.itemName || e.vendorCategoryName || 'Expense'}
              getSubtitle={(e) => (e.itemName ? e.vendorCategoryName : null)}
              placeholder="Choose the item this is for"
              ariaLabel="Expense"
            />
          </div>
        )}
        <div>
          <label htmlFor="vendor-doc-file" className="block text-sm font-medium text-gray-700 mb-1">
            File
          </label>
          <input
            key={inputKey}
            id="vendor-doc-file"
            type="file"
            accept={uploadAccept([...VENDOR_DOCUMENT_FILE_TYPES])}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm"
          />
          <AcceptedFileTypesNote fileTypes={VENDOR_DOCUMENT_FILE_TYPES} />
        </div>
        <button
          onClick={handleUpload}
          disabled={!file || !expenseItemId || uploadMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 min-h-11 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Upload size={16} />
          {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
        </button>
        {message && (
          <p
            role={message.tone === 'error' ? 'alert' : 'status'}
            className={`text-sm ${message.tone === 'error' ? 'text-red-600' : 'text-gray-600'}`}
          >
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}

function VendorProfilePanel() {
  const { data: profile, isLoading } = useVendorProfile();
  const updateProfile = useUpdateVendorProfile();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<VendorProfile>>({});

  if (isLoading) return <p className="text-sm text-gray-500">Loading profile...</p>;
  if (!profile) return null;

  function startEditing() {
    setForm({
      businessName: profile!.businessName,
      phone: profile!.phone || '',
      email: profile!.email || '',
      website: profile!.website || '',
    });
    setEditing(true);
  }

  function handleSave() {
    updateProfile.mutate(
      {
        businessName: form.businessName,
        phone: form.phone || undefined,
        email: form.email || undefined,
        website: form.website || undefined,
      },
      {
        onSuccess: () => setEditing(false),
      },
    );
  }

  return (
    <div data-testid="vendor-profile" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <User size={18} />
        Business Profile
      </h3>

      {!editing ? (
        <div className="space-y-2 text-sm">
          <p>
            <strong>Business Name:</strong> {profile.businessName}
          </p>
          {profile.phone && (
            <p>
              <strong>Phone:</strong> <PhoneLink phone={profile.phone} whatsapp={false} />
            </p>
          )}
          {profile.email && (
            <p>
              <strong>Email:</strong> {profile.email}
            </p>
          )}
          {profile.website && (
            <p>
              <strong>Website:</strong> {profile.website}
            </p>
          )}
          <button
            onClick={startEditing}
            className="mt-3 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Edit Profile
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="vendor-business-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Name
            </label>
            <input
              id="vendor-business-name"
              type="text"
              value={form.businessName || ''}
              onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label htmlFor="vendor-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <PhoneInput
              id="vendor-phone"
              value={form.phone || ''}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label htmlFor="vendor-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="vendor-email"
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="vendor-website"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website
            </label>
            <input
              id="vendor-website"
              type="url"
              value={form.website || ''}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={updateProfile.isPending}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {updateProfile.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function VendorDashboardPage() {
  const { data: events, isLoading, refetch } = useVendorEvents();
  const refreshMyTasks = useRefreshMyPortalTasks();
  // The chosen event is in the address (`?event=`), so a task email's link opens it.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('event');
  const selectedEvent = events?.find((event) => event.eventId === selectedId) ?? null;
  // Arriving by a link (a task notice, an email) fetches the list and the
  // tasks again, once per event asked for: what the link is about — often a
  // task assigned a moment ago — may be missing from the cached copy, or the
  // event cached without its tasks shared yet.
  const refreshedFor = useRef<string | null>(null);
  const arrived = !isLoading && !!selectedId;
  useEffect(() => {
    if (!arrived || refreshedFor.current === selectedId) return;
    refreshedFor.current = selectedId;
    void refetch();
    void refreshMyTasks();
  }, [arrived, selectedId, refetch, refreshMyTasks]);
  const setSelectedEvent = (event: VendorEvent) => {
    // Chosen on this page, from the list it already shows: nothing to refetch.
    refreshedFor.current = event.eventId;
    setSearchParams(
      (params) => {
        params.set('event', event.eventId);
        return params;
      },
      { replace: true },
    );
  };

  if (isLoading) {
    return <p className="p-4 text-gray-500">Loading your events...</p>;
  }

  if (!events || events.length === 0) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Vendor Dashboard</h1>
        <p className="text-gray-500">Nothing has been shared with you yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Vendor Dashboard</h1>

      {/* Vendor Profile */}
      <VendorProfilePanel />

      <MyTasksCard scope="vendor" eventPath={(id) => `/vendor?event=${id}`} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((event) => (
          <VendorEventCard
            key={event.eventId}
            event={event}
            onSelect={setSelectedEvent}
            isSelected={selectedEvent?.eventId === event.eventId}
          />
        ))}
      </div>

      {selectedEvent && (
        <div className="space-y-4" data-testid="vendor-event-detail">
          <h2 className="text-xl font-semibold">{selectedEvent.eventName}</h2>

          {/* Each of the person's bookings on the event leads, read-only. */}
          {selectedEvent.bookings.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="vendor-bookings">
              {selectedEvent.bookings.map((booking) => (
                <VendorBookingCard
                  key={booking.vendorId}
                  booking={booking}
                  eventName={selectedEvent.eventName}
                  eventDate={selectedEvent.eventDate}
                  venue={selectedEvent.venue}
                  showVendorName={selectedEvent.bookings.length > 1}
                />
              ))}
            </div>
          )}

          {selectedEvent.access.tasks && (
            // Keyed by event: a half-typed task belongs to the event it was started on.
            <ClientTaskList
              key={selectedEvent.eventId}
              eventId={selectedEvent.eventId}
              eventStartAt={selectedEvent.eventDate}
              scope="vendor"
              canAdd={selectedEvent.access.addTasks}
              cancelled={selectedEvent.cancelled}
            />
          )}

          {/*
            Only what the office shared: the server refuses every other panel
            for this event, so none is drawn.
          */}
          {(selectedEvent.access.expenses || selectedEvent.access.timeline) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {selectedEvent.access.expenses && (
                <div className="bg-white rounded-lg border p-4">
                  <VendorExpensesPanel eventId={selectedEvent.eventId} />
                </div>
              )}
              {selectedEvent.access.timeline && (
                <div className="bg-white rounded-lg border p-4">
                  <VendorTimelinePanel eventId={selectedEvent.eventId} />
                </div>
              )}
            </div>
          )}

          {selectedEvent.access.expenses && (
            <div className="bg-white rounded-lg border p-4">
              {/* Keyed by event: a choice of expense belongs to the event it was made on. */}
              <VendorDocumentUploadPanel
                key={selectedEvent.eventId}
                eventId={selectedEvent.eventId}
              />
            </div>
          )}

          {selectedEvent.access.items && (
            <div className="bg-white rounded-lg border p-4">
              <VendorItemsPanel key={selectedEvent.eventId} eventId={selectedEvent.eventId} />
            </div>
          )}

          {selectedEvent.access.folders.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <VendorFilesPanel
                key={selectedEvent.eventId}
                folders={selectedEvent.access.folders}
              />
            </div>
          )}

          {selectedEvent.access.floorPlans.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <VendorFloorPlansPanel
                key={selectedEvent.eventId}
                eventId={selectedEvent.eventId}
                eventName={selectedEvent.eventName}
              />
            </div>
          )}

          {selectedEvent.canMessage && (
            // Keyed by the event: switching events starts a fresh list and
            // closes any draft, rather than carrying either across.
            <PortalConversations
              key={selectedEvent.eventId}
              entityType="Event"
              entityId={selectedEvent.eventId}
              recordName={selectedEvent.eventName}
            />
          )}
        </div>
      )}
    </div>
  );
}
